// The prize pool, the earning rules, and what's waiting for a grown-up (mockups o03, o08).
// Pure folds over the event log, in server order.
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { PlaceWanted, type OwnPlace } from '../../shared/places'
import { DEFAULT_RULES, Prize, PRIZE_CHANCE, PRIZE_ODDS, PrizeRedeemed, PrizeSet, PrizeWon, RETIRED_PRIZES, RewardAwarded, RewardRulesSet, STARTER_PRIZES, type Rarity, type RewardRules } from '../../shared/rewards'
import { grownUpPlayer, playersOf } from './players'
import { askedTasks } from './tasks'
import { rng } from './rng'
import { tripEventOrder, type TripEvent } from './trip/project'
import { packAsks } from './packs'

export interface PoolPrize extends Prize {
  id: string
  /** Times won so far (all players). */
  won: number
}

const ordered = (events: readonly TripEvent[], types: string[]) => events.filter((e) => types.includes(e.type)).sort(tripEventOrder)

export function prizePool(events: readonly TripEvent[]): PoolPrize[] {
  const rows = new Map<string, Record<string, unknown>>(Object.entries(STARTER_PRIZES).map(([id, p]) => [id, { ...p }]))
  const won = new Map<string, number>()
  for (const e of ordered(events, ['prize.set', 'prize.removed', 'prize.won'])) {
    if (e.type === 'prize.set') {
      const p = PrizeSet.safeParse(e.payload)
      if (p.success) rows.set(p.data.prizeId, { ...(rows.get(p.data.prizeId) ?? RETIRED_PRIZES[p.data.prizeId]), ...p.data.fields })
    } else if (e.type === 'prize.removed') {
      rows.delete((e.payload as { prizeId?: string }).prizeId ?? '')
    } else {
      const p = PrizeWon.safeParse(e.payload)
      if (p.success) won.set(p.data.prizeId, (won.get(p.data.prizeId) ?? 0) + 1)
    }
  }
  const out: PoolPrize[] = []
  for (const [id, raw] of rows) {
    const p = Prize.safeParse(raw)
    if (p.success) out.push({ ...p.data, id, won: won.get(id) ?? 0 })
  }
  return out
}

/** A won prize's details: from the pool, or a retired starter prize (it still shows its title). */
function wonPrize(byId: ReadonlyMap<string, PoolPrize>, prizeId: string): PoolPrize | null {
  const p = byId.get(prizeId)
  if (p) return p
  const old = RETIRED_PRIZES[prizeId]
  return old ? { ...old, id: prizeId, won: 0, paused: true } : null
}

/** Prizes a player could win right now: theirs, not paused, not used up. A grown-up has no age band, so only "all" and named prizes. */
export function prizesFor(pool: readonly PoolPrize[], child: Pick<Person, 'id' | 'ageBand'>): PoolPrize[] {
  return pool.filter((p) => {
    if (p.paused || (p.cap !== undefined && p.won >= p.cap)) return false
    if (p.who === 'all') return true
    if (Array.isArray(p.who)) return p.who.includes(child.id)
    return p.who === child.ageBand
  })
}

function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619)
  return h >>> 0
}

/**
 * What a finished capsule holds, decided once on the phone and recorded as `prize.won`: with
 * `chance` a prize from the child's pool, its rarity by `odds` (70 / 25 / 5); if the pool has
 * nothing of that rarity, or the roll misses, a sticker (null). Seeded by the capsule, so the
 * same capsule always gives the same answer.
 */
export function drawPrize(sessionId: string, prizes: readonly PoolPrize[], chance = PRIZE_CHANCE, odds: Record<Rarity, number> = PRIZE_ODDS): PoolPrize | null {
  const rand = rng(hash(`prize|${sessionId}`))
  if (rand() >= chance || !prizes.length) return null
  const total = Object.values(odds).reduce((s, n) => s + n, 0)
  let roll = rand() * total
  let rarity: Rarity = 'common'
  for (const r of ['common', 'rare', 'legendary'] as const) {
    rarity = r
    if ((roll -= odds[r]) < 0) break
  }
  const tier = prizes.filter((p) => p.rarity === rarity)
  return tier.length ? rand.pick(tier) : null
}

export interface MyPrize {
  wonId: string
  prize: PoolPrize | null
  at: number
  /** When it was cashed in (stickers and toys pay out at once, and a grown-up player's real prizes too). */
  cashedAt: number | null
  note?: string
}

/**
 * A player's prizes (mockup r02): waiting for a grown-up, cashed in, and what's still in the
 * machine. A grown-up who plays has nobody to ask, so their real prizes are theirs at once (WI-37).
 */
export function myPrizes(events: readonly TripEvent[], child: Pick<Person, 'id' | 'ageBand'> & Partial<Pick<Person, 'kind' | 'plays'>>) {
  const selfCash = child.kind !== undefined && grownUpPlayer({ kind: child.kind, plays: child.plays })
  const pool = prizePool(events)
  const byId = new Map(pool.map((p) => [p.id, p]))
  const settled = new Map<string, { at: number; note?: string }>()
  for (const e of ordered(events, ['prize.redeemed'])) {
    const p = PrizeRedeemed.safeParse(e.payload)
    if (!p.success) continue
    if (p.data.decision === 'later') settled.delete(p.data.wonId)
    else settled.set(p.data.wonId, { at: e.at, ...(p.data.note ? { note: p.data.note } : {}) })
  }
  const mine: MyPrize[] = []
  for (const e of ordered(events, ['prize.won'])) {
    const p = PrizeWon.safeParse(e.payload)
    if (!p.success || p.data.for !== child.id) continue
    const prize = wonPrize(byId, p.data.prizeId)
    const s = settled.get(p.data.wonId)
    const instant = selfCash || (prize && prize.kind !== 'real')
    mine.push({ wonId: p.data.wonId, prize, at: e.at, cashedAt: s?.at ?? (instant ? e.at : null), ...(s?.note ? { note: s.note } : {}) })
  }
  // In the machine: theirs, including paused ones (shown greyed out), not used up.
  const inMachine = prizesFor(pool.map((p) => ({ ...p, paused: false })), child).map((p) => ({ ...p, paused: !!byId.get(p.id)?.paused }))
  return {
    waiting: mine.filter((m) => m.cashedAt === null).reverse(),
    cashed: mine.filter((m) => m.cashedAt !== null).sort((a, b) => b.cashedAt! - a.cashedAt!),
    inMachine,
  }
}

export function rewardRules(events: readonly TripEvent[]): RewardRules {
  const r: RewardRules = structuredClone(DEFAULT_RULES)
  for (const e of ordered(events, ['rewards.rules.set'])) {
    const p = RewardRulesSet.safeParse(e.payload)
    if (p.success) Object.assign(r, p.data.fields)
  }
  return r
}

export type NeedsYou =
  | { kind: 'prize'; key: string; wonId: string; childId: string; prize: PoolPrize | null; at: number }
  | { kind: 'activity'; key: string; itemId: string; title: string; date: string; childId: string; plays: number; at: number }
  | { kind: 'suggestion'; key: string; eventId: string; childId: string; placeId: string; place?: OwnPlace; at: number }
  | { kind: 'task'; key: string; taskId: string; childId: string; title: string; emoji: string; plays: number; at: number }
  | { kind: 'pack'; key: string; packId: string; childId: string; at: number }

/**
 * What's waiting for a grown-up, newest first: real prizes to cash in, activities with an
 * award that are due (today or earlier) and not yet confirmed, and children's want-to-go places
 * that aren't in the plan yet. Activities are for every player (WI-37) except `me`: nobody
 * confirms their own. A grown-up player's prizes are self-cashed, so they never wait here.
 */
/** `owned`: the family's packs (C6), so a child's ask to unwrap one waits here until it's bought. */
export function needsYou(pack: Pack, people: readonly Pick<Person, 'id' | 'kind' | 'plays'>[], events: readonly TripEvent[], today: string, me?: string, owned?: readonly string[] | null): NeedsYou[] {
  const kids = people.filter((p) => p.kind === 'child').map((p) => p.id)
  const players = playersOf(people).map((p) => p.id).filter((id) => id !== me)
  const selfCashers = new Set(people.filter(grownUpPlayer).map((p) => p.id))
  const pool = new Map(prizePool(events).map((p) => [p.id, p]))
  const out: NeedsYou[] = []

  const settled = new Set(ordered(events, ['prize.redeemed']).flatMap((e) => {
    const p = PrizeRedeemed.safeParse(e.payload)
    return p.success && p.data.decision !== 'later' ? [p.data.wonId] : []
  }))
  for (const e of ordered(events, ['prize.won'])) {
    const p = PrizeWon.safeParse(e.payload)
    if (!p.success || settled.has(p.data.wonId) || selfCashers.has(p.data.for)) continue
    const prize = wonPrize(pool, p.data.prizeId)
    if (prize && prize.kind !== 'real') continue
    out.push({ kind: 'prize', key: `prize:${p.data.wonId}`, wonId: p.data.wonId, childId: p.data.for, prize, at: e.at })
  }

  const awarded = new Set(ordered(events, ['reward.awarded']).flatMap((e) => {
    const p = RewardAwarded.safeParse(e.payload)
    return p.success ? [`${p.data.itemId}|${p.data.for}`] : []
  }))
  for (const item of pack.itinerary.dayItems) {
    if (!item.award || item.date > today) continue
    for (const childId of item.who?.filter((w) => players.includes(w)) ?? players) {
      if (awarded.has(`${item.id}|${childId}`)) continue
      out.push({ kind: 'activity', key: `activity:${item.id}:${childId}`, itemId: item.id, title: item.title, date: item.date, childId, plays: item.award.plays, at: Date.parse(`${item.date}T23:59:00Z`) })
    }
  }

  // Holiday tasks a child asked a grown-up to confirm (WI-13b); nobody confirms their own.
  for (const a of askedTasks(events)) {
    if (!players.includes(a.childId) || !a.task) continue
    out.push({ kind: 'task', key: a.key, taskId: a.taskId, childId: a.childId, title: a.task.title, emoji: a.task.emoji, plays: a.task.plays, at: a.at })
  }

  const planned = new Set(pack.itinerary.dayItems.flatMap((i) => (i.placeId ? [i.placeId] : [])))
  const resolved = new Set(ordered(events, ['suggestion.resolved']).map((e) => (e.payload as { suggestionId?: string }).suggestionId))
  // A want the child took back after it was made isn't waiting any more.
  const lastUnwant = new Map<string, number>()
  for (const e of ordered(events, ['place.unwanted'])) lastUnwant.set(`${e.personId}|${(e.payload as { placeId?: string } | null)?.placeId}`, e.at)
  for (const e of ordered(events, ['place.wanted'])) {
    const p = PlaceWanted.safeParse(e.payload)
    if (!p.success || !kids.includes(e.personId) || planned.has(p.data.placeId) || resolved.has(e.id)) continue
    if ((lastUnwant.get(`${e.personId}|${p.data.placeId}`) ?? -1) >= e.at) continue
    out.push({ kind: 'suggestion', key: `suggestion:${e.id}`, eventId: e.id, childId: e.personId, placeId: p.data.placeId, ...(p.data.place ? { place: p.data.place } : {}), at: e.at })
  }
  if (owned) for (const a of packAsks(events, owned, players)) out.push({ kind: 'pack', ...a })
  return out.sort((a, b) => b.at - a.at)
}
